'use client';

import { forwardRef } from 'react';
import { Radio as MantineRadio } from '@mantine/core';
import type {
  RadioCardProps as MantineRadioCardProps,
  RadioGroupProps as MantineRadioGroupProps,
  RadioProps as MantineRadioProps,
} from '@mantine/core';

import type { LockedProps } from '../types';

export type RadioProps = Omit<MantineRadioProps, LockedProps>;

export type RadioGroupProps = Omit<MantineRadioGroupProps, LockedProps>;

export type RadioCardProps = Omit<MantineRadioCardProps, LockedProps>;

const RadioRoot = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  return <MantineRadio ref={ref} {...props} />;
});

RadioRoot.displayName = 'Radio';

const RadioGroup = (props: RadioGroupProps) => <MantineRadio.Group {...props} />;

RadioGroup.displayName = 'Radio.Group';

const RadioCard = forwardRef<HTMLButtonElement, RadioCardProps>((props, ref) => (
  <MantineRadio.Card ref={ref} {...props} />
));

RadioCard.displayName = 'Radio.Card';

type RadioRootComponent = typeof RadioRoot;

interface RadioCompound extends RadioRootComponent {
  /**
   * Group wrapper for mutually exclusive radio options with shared value state.
   * Children render as-is; wrap them in a `Stack`/`Group` for layout.
   *
   * @example
   * <Radio.Group value={selected} onChange={setSelected}>
   *   <Stack gap="xs" mt="xs">
   *     <Radio value="a" label="Option A" />
   *   </Stack>
   * </Radio.Group>
   */
  Group: typeof RadioGroup;
  /**
   * Card-style clickable radio option. Use as a child of `Radio.Group`; render the label/content
   * as children.
   *
   * @example
   * <Radio.Group value={value} onChange={setValue}>
   *   <Radio.Card value="a" radius="md" px="sm" py="xs">
   *     <Text>Option A</Text>
   *   </Radio.Card>
   * </Radio.Group>
   */
  Card: typeof RadioCard;
}

/**
 * ### Radio
 *
 * @description
 * Mantine's Radio minus per-call-site style overrides (see {@link LockedProps}).
 * Use `Radio.Group` for grouped exclusive selection.
 *
 * @example
 * <Radio.Group value={value} onChange={setValue}>
 *   <Stack gap="xs" mt="xs">
 *     <Radio value="a" label="Option A" />
 *   </Stack>
 * </Radio.Group>
 *
 * @param props - {@link RadioProps}
 * @returns Compound radio component (`Radio`, `Radio.Group`).
 */
export const Radio: RadioCompound = Object.assign(RadioRoot, {
  Group: RadioGroup,
  Card: RadioCard,
});
